/* eslint-disable @typescript-eslint/no-explicit-any */
// we are importing lodash directly here due to a bug found when running jest coverage.
import * as React from 'react';
import * as Service from 'types/service';
import { AbstractBooking, bookingIsPurchase } from 'utils/timeline/typeGuards';
import { BookedCalendarSquare, WalkInCalendarSquare } from 'types/tableBasedInventory';
import { CalendarItem, WalkIn } from './calendarItems';
import { ChronoUnit, Instant, LocalDate, LocalDateTime, ZoneId } from 'js-joda';
import { DEFAULT_WALK_IN_NAME } from 'utils/service/walkInUtils';
import { DisplayTime } from '@tock/time';
import { IServiceObject } from './getServiceDayDetails';
import { IconArrowDownCircle, IconPersonCheck } from '@tock/icons';
import { PartyIdToPartyMap } from 'selectors/partiesById';
import { Purchase, TicketTypeVariety, WalkIn as WalkInBooking } from 'generated/graphql';
import { RealtimePartyDetails } from 'middleware/realtimePos';
import { ServiceDefinedState } from 'state/service/config';
import { StateSelectorStatus } from 'components/service/party-panel/PartyPanelFooter';
import { assertNever } from '@tock/shared/utils/typeUtils';
import {
  capitalizeName,
  convertFullNameToFirstLast as convertFullNameToFirstLastShared,
} from '@tock/shared/utils/patron';
import { colorList } from 'utils/colorUtils';
import { colorToClassName } from 'constants/partyColorConstants';
import { core, safeEnumCompare } from '@tock/coretypes';
import { dashboard } from 'types';
import { fastParseDateTime, isValidDateTimeString, roundTo15MinInterval } from 'utils/timeUtils';
import { findNoteWithNoteType } from 'utils/noteUtils';
import {
  getLoyaltyPointsRedeemed,
  visitDietaryNotes,
  visitGeneralNotes,
} from 'utils/purchaseUtils';
import { getTagsForProfile } from 'utils/tagUtils';
import {
  isDeliveryVariety,
  isOrderVariety,
  isThirdPartyDeliveryVariety,
} from '@tock/shared/utils/offerings';
import { logger } from '@tock/logging';
import { oc } from 'ts-optchain';
import { toLocalDateTime } from '@tock/shared/utils/time';
import PartyStates from 'constants/partyStateConstants';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _findIndex from 'lodash/findIndex';
import _forEach from 'lodash/forEach';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _isUndefined from 'lodash/isUndefined';
import _join from 'lodash/join';
import _map from 'lodash/map';
import _min from 'lodash/min';
import _noop from 'lodash/noop';
import _trim from 'lodash/trim';
import _union from 'lodash/union';
import confirm from 'shared/utils/confirm';
import protos from 'utils/protos';

const { TextMessage } = protos;

export const FALLBACK_SERVICE_TYPE = 'UNKNOWN';
export const DEFAULT_ORDERING = -1;
export const EXPECTED_ORDERING = -3;
export const ARRIVED_ORDERING = -2;
export const PARTIALLY_ARRIVED_ORDERING = -1;
export const PARTIALLY_SEATED_ORDERING = 0;

export function checkHasUnreadTextMessage(textMessage: dashboard.TextMessage[]) {
  return !_isEmpty(
    _filter(textMessage, (textMessage) => {
      return (
        !textMessage.isRead &&
        safeEnumCompare(
          textMessage.status,
          TextMessage.TextStatus,
          TextMessage.TextStatus.RECEIVED
        ) &&
        safeEnumCompare(
          textMessage.messageType,
          TextMessage.MessageType,
          TextMessage.MessageType.SERVICE_TEXT
        )
      );
    })
  );
}

// TODO: implementation has moved to shared/utils/patron. This should be removed and any references rerouted later
export function convertFullNameToFirstLast(name: any, { reverse = false }: { reverse: boolean }) {
  return convertFullNameToFirstLastShared(name, { reverse: reverse });
}

export function getFullName({
  firstName,
  lastName,
  email,
  lastNameFirst,
}: {
  firstName?: string;
  lastName?: string;
  email?: string;
  lastNameFirst?: boolean;
}): string {
  const firstNameTrim = _trim(firstName);
  const lastNameTrim = _trim(lastName);
  if (firstNameTrim && lastNameTrim) {
    return lastNameFirst
      ? `${capitalizeName(lastNameTrim)}, ${capitalizeName(firstNameTrim)}`
      : `${firstNameTrim} ${lastNameTrim}`;
  } else if (!firstNameTrim && lastNameTrim) {
    return lastNameTrim;
  } else if (!lastNameTrim && firstNameTrim) {
    return firstNameTrim;
  } else {
    return _trim(email);
  }
}

export function getInferredServiceTypeString(
  serviceObjects: IServiceObject[] | undefined,
  timeToCompare: LocalDateTime
): core.Service.Type | undefined {
  const serviceContainingExpectedTime: IServiceObject | undefined = _find(
    serviceObjects,
    (hour: IServiceObject) => {
      return !timeToCompare.isBefore(hour.startTime) && timeToCompare.isBefore(hour.endTime);
    }
  );

  if (serviceContainingExpectedTime) {
    return getServiceTypeFromString(serviceContainingExpectedTime.serviceType);
  } else {
    let closestTimeDelta = Infinity;
    let closestServiceType;

    _forEach(serviceObjects, (serviceHoursObject) => {
      const distanceFromStartTime = Math.abs(
        timeToCompare.until(serviceHoursObject.startTime, ChronoUnit.MINUTES)
      );
      const distanceFromEndTime = Math.abs(
        timeToCompare.until(serviceHoursObject.endTime, ChronoUnit.MINUTES)
      );
      const closestDistance = _min([distanceFromStartTime, distanceFromEndTime]);

      if (!_isNil(closestDistance) && closestDistance <= closestTimeDelta) {
        closestTimeDelta = closestDistance;
        closestServiceType = serviceHoursObject.serviceType;
      }
    });

    if (_isNil(closestServiceType)) {
      return;
    }

    return getServiceTypeFromString(closestServiceType);
  }
}

function getServiceTypeFromString(serviceType: string): core.Service.Type {
  if (serviceType === 'BREAKFAST') {
    return serviceType;
  } else if (serviceType === 'BRUNCH') {
    return serviceType;
  } else if (serviceType === 'LUNCH') {
    return serviceType;
  } else if (serviceType === 'DINNER') {
    return serviceType;
  } else {
    // Use Dinner as fallback to avoid crashing the app if an undefined
    // service type slips in here.
    return 'DINNER';
  }
}

export function getLastName(patron?: {
  firstName?: string;
  lastName?: string;
  email?: string;
}): string {
  if (!patron) {
    return '';
  }
  const firstName = _trim(patron.firstName);
  const lastName = _trim(patron.lastName);
  const email = _trim(patron.email);

  if (lastName) {
    return lastName;
  } else if (firstName) {
    return firstName;
  } else if (email) {
    return email;
  } else {
    return DEFAULT_WALK_IN_NAME;
  }
}

export function getNameForParty({
  firstName,
  lastName,
  email,
  firstNameFirst,
  partyIsOnlineWalkIn,
}: {
  firstName?: string;
  lastName?: string;
  email?: string;
  firstNameFirst?: boolean;
  partyIsOnlineWalkIn?: boolean;
}): string {
  const firstNameTrim = _trim(firstName);
  const lastNameTrim = _trim(lastName);
  const emailTrim = _trim(email);
  return getName(firstNameTrim, lastNameTrim, emailTrim, firstNameFirst, partyIsOnlineWalkIn);
}

export function getName(
  firstName: string,
  lastName: string,
  email: string,
  firstNameFirst?: boolean,
  partyIsOnlineWalkIn?: boolean
): string {
  if (firstName && lastName) {
    if (firstNameFirst) {
      return `${firstName} ${lastName}`;
    } else {
      return `${lastName}, ${firstName}`;
    }
  } else if (!firstName && lastName) {
    return lastName;
  } else if (!lastName && firstName) {
    return firstName;
  } else if (!email) {
    if (partyIsOnlineWalkIn) {
      return `${DEFAULT_WALK_IN_NAME} (Online)`;
    }
    return DEFAULT_WALK_IN_NAME;
  } else {
    return email;
  }
}

export function getCustomDisplayInformation(
  businessDefinedState: ServiceDefinedState,
  primaryState: Service.PartyState
): Service.StateDescription {
  const classNameColor = colorToClassName[businessDefinedState.color];
  return {
    aerialTableBackgroundClassName: classNameColor.aerialTableBackground,
    aerialTableForegroundClassName: classNameColor.aerialTableForeground,
    borderClassName: classNameColor.borderColor,
    className: classNameColor.backgroundColor,
    ordering: businessDefinedState.ordering,
    secondaryValue: null,
    stateName: getStateName(primaryState),
    strokeColorClassName: classNameColor.strokeColor,
    textClassName: classNameColor.textColor,
    primaryState,
  };
}

export const getVariableColorFromBusinessStringColor = (color: string) => {
  const name = colorList.find(({ color: listColor }) => listColor === color)?.name;

  if (name) {
    const colorArray = name.toLowerCase().split(' ');
    return `var(--spec-${colorArray.join('-')})`;
  } else {
    return `var(--spec-${color})`;
  }
};

export const getDefaultEmotionDisplayInformation = (
  primaryState: Service.PartyState
): ServiceDefinedState => {
  const stateName = getStateName(primaryState);
  switch (primaryState) {
    case 'EXPECTED':
      return {
        primaryState: PartyStates.expected,
        stateName,
        color: 'var(--spec-grey)',
        ordering: 0,
      };
    case 'SEATED':
      return {
        primaryState: PartyStates.seated,
        stateName,
        color: 'var(--spec-lime)',
        ordering: 0,
      };
    case 'PARTIALLY_ARRIVED':
      return {
        primaryState: PartyStates.partiallyarrived,
        stateName,
        color: 'var(--spec-pink)',
        ordering: 0,
      };
    case 'ARRIVED':
      return {
        primaryState: PartyStates.arrived,
        stateName,
        color: 'var(--spec-amber)',
        ordering: 0,
      };
    case 'PARTIALLY_SEATED':
      return {
        primaryState: PartyStates.partiallyseated,
        stateName,
        color: 'var(--spec-yellow)',
        ordering: 0,
      };
    case 'CANCELED':
      return {
        primaryState: PartyStates.canceled,
        stateName,
        color: 'var(--spec-red)',
        ordering: 0,
      };
    case 'LEFT':
      return {
        primaryState: PartyStates.left,
        stateName,
        color: 'var(--spec-lavender)',
        ordering: 0,
      };
    case 'NO_SHOW':
      return {
        primaryState: PartyStates.noshow,
        stateName,
        color: 'var(--spec-red)',
        ordering: 0,
      };
    default:
      assertNever(primaryState);
  }
};

export function getDefaultDisplayInformation(
  primaryState: Service.PartyState
): Service.StateDescription {
  const stateName = getStateName(primaryState);
  if (primaryState === PartyStates.expected) {
    return {
      aerialTableBackgroundClassName: colorToClassName.grey.aerialTableBackground,
      aerialTableForegroundClassName: colorToClassName.grey.aerialTableForeground,
      borderClassName: colorToClassName.grey.borderColor,
      className: colorToClassName.grey.backgroundColor,
      ordering: EXPECTED_ORDERING,
      primaryState,
      secondaryValue: null,
      stateName,
      strokeColorClassName: colorToClassName.grey.strokeColor,
      textClassName: colorToClassName.grey.textColor,
    };
  } else if (primaryState === PartyStates.seated) {
    return {
      aerialTableBackgroundClassName: colorToClassName.lime.aerialTableBackground,
      aerialTableForegroundClassName: colorToClassName.lime.aerialTableForeground,
      borderClassName: colorToClassName.lime.borderColor,
      className: colorToClassName.lime.backgroundColor,
      ordering: DEFAULT_ORDERING,
      primaryState,
      secondaryValue: null,
      stateName,
      strokeColorClassName: colorToClassName.lime.strokeColor,
      textClassName: colorToClassName.lime.textColor,
    };
  } else if (primaryState === PartyStates.left) {
    return {
      aerialTableBackgroundClassName: colorToClassName.lavender.aerialTableBackground,
      aerialTableForegroundClassName: colorToClassName.lavender.aerialTableForeground,
      borderClassName: colorToClassName.lavender.borderColor,
      className: colorToClassName.lavender.backgroundColor,
      ordering: DEFAULT_ORDERING,
      primaryState,
      secondaryValue: null,
      stateName,
      strokeColorClassName: colorToClassName.lavender.strokeColor,
      textClassName: colorToClassName.lavender.textColor,
    };
  } else if (primaryState === PartyStates.noshow || primaryState === PartyStates.canceled) {
    return {
      aerialTableBackgroundClassName: colorToClassName.red.aerialTableBackground,
      aerialTableForegroundClassName: colorToClassName.red.aerialTableForeground,
      borderClassName: colorToClassName.red.borderColor,
      className: colorToClassName.red.backgroundColor,
      ordering: DEFAULT_ORDERING,
      primaryState,
      secondaryValue: null,
      stateName,
      strokeColorClassName: colorToClassName.red.strokeColor,
      textClassName: colorToClassName.red.textColor,
    };
  } else if (primaryState === PartyStates.arrived) {
    return {
      aerialTableBackgroundClassName: colorToClassName.amber.aerialTableBackground,
      aerialTableForegroundClassName: colorToClassName.amber.aerialTableForeground,
      borderClassName: colorToClassName.amber.borderColor,
      className: colorToClassName.amber.backgroundColor,
      ordering: ARRIVED_ORDERING,
      primaryState,
      secondaryValue: null,
      stateName,
      strokeColorClassName: colorToClassName.amber.strokeColor,
      textClassName: colorToClassName.amber.textColor,
    };
  } else if (primaryState === PartyStates.partiallyarrived) {
    return {
      aerialTableBackgroundClassName: colorToClassName.pink.aerialTableBackground,
      aerialTableForegroundClassName: colorToClassName.pink.aerialTableForeground,
      borderClassName: colorToClassName.pink.borderColor,
      className: colorToClassName.pink.backgroundColor,
      ordering: PARTIALLY_ARRIVED_ORDERING,
      primaryState,
      secondaryValue: null,
      stateName,
      strokeColorClassName: colorToClassName.pink.strokeColor,
      textClassName: colorToClassName.pink.textColor,
    };
  } else if (primaryState === PartyStates.partiallyseated) {
    return {
      aerialTableBackgroundClassName: colorToClassName.yellow.aerialTableBackground,
      aerialTableForegroundClassName: colorToClassName.yellow.aerialTableForeground,
      borderClassName: colorToClassName.yellow.borderColor,
      className: colorToClassName.yellow.backgroundColor,
      ordering: PARTIALLY_SEATED_ORDERING,
      primaryState,
      secondaryValue: null,
      stateName,
      strokeColorClassName: colorToClassName.yellow.strokeColor,
      textClassName: colorToClassName.yellow.textColor,
    };
  } else {
    return assertNever(primaryState);
  }
}

/*
** Uses custom style options for the state or falls beack to defaults if those
are not available or not enabled
** @param primaryState - the enum for which type of state this is
** @param businessDefinedState - the customized options for a smart state that may or not be present
depending on if the business has changed them
** @param useSmartState - check the feature flag for using smart states
*/
export function getDisplayInfoForPartyState(
  primaryState: Service.PartyState,
  businessDefinedState?: ServiceDefinedState
): Service.StateDescription {
  return businessDefinedState
    ? getCustomDisplayInformation(businessDefinedState, primaryState)
    : getDefaultDisplayInformation(primaryState);
}

export function getStateName(primaryState: Service.PartyState | dashboard.PartyState) {
  switch (primaryState) {
    case PartyStates.expected:
      return 'Expected';
    case PartyStates.seated:
      return 'Seated';
    case PartyStates.left:
      return 'Finished';
    case PartyStates.noshow:
      return 'No-show';
    case PartyStates.canceled:
      return 'Canceled';
    case PartyStates.arrived:
      return 'Arrived';
    case PartyStates.partiallyarrived:
      return 'Partially Arrived';
    case PartyStates.partiallyseated:
      return 'Partially Seated';
    default:
      logger.warn(`Unexpected partyState ${primaryState}`);
      return '';
  }
}

export function getRoomsForParty(party: Service.Party): string[] {
  return _filter(_map(party.tables, (table) => oc(table).room())) as string[];
}

export function getRoomIdsForParty(party: Service.Party): number[] {
  return _filter(_map(party.tableIds));
}

export function hasExpectedBookingTime(party: Service.Party): boolean {
  if (!party) {
    return false;
  }
  return isPartyExpected(party) && !!party.expectedReservationTime;
}

export function hasPartyLeft(party: Service.Party): boolean {
  if (!party) {
    return false;
  }
  return party.state === PartyStates.left;
}

export function isOnWaitList(party: Service.Party): boolean {
  if (!party) {
    return false;
  }
  return isPartyExpected(party) && !party.expectedReservationTime;
}

export function isPartyCanceled(party: Service.Party): boolean {
  if (!party) {
    return false;
  }
  return party.state === PartyStates.canceled;
}

export function isPartyDining(party: Service.Party): boolean {
  if (!party) {
    return false;
  }
  return isPartySeated(party) || hasExpectedBookingTime(party) || isPartyPartiallySeated(party);
}

export function isPartyFinished(party: Service.Party): boolean {
  if (!party) {
    return false;
  }
  return party.state === PartyStates.left;
}

export function isPartyPreassigned(party: Service.Party): boolean {
  if (!party) {
    return false;
  }

  return isPartyExpected(party) && !_isEmpty(party.tableIds);
}

export function isPartyExpected(party: Service.Party): boolean {
  if (!party) {
    return false;
  }
  return (
    party.state === PartyStates.expected ||
    party.state === PartyStates.partiallyarrived ||
    party.state === PartyStates.partiallyseated ||
    party.state === PartyStates.arrived
  );
}

export function isPartyNotComing(party?: Service.Party): boolean {
  if (!party) {
    return false;
  }
  return party.state === PartyStates.noshow;
}

export const isPartyNoShow = isPartyNotComing;

export function isPartySeated(party?: Service.Party): boolean {
  if (!party) {
    return false;
  }
  return party.state === PartyStates.seated;
}

export function isPartyPartiallySeated(party?: Service.Party): boolean {
  if (!party) {
    return false;
  }
  return party.state === PartyStates.partiallyseated;
}

export function isPartyArrived(party?: Service.Party): boolean {
  if (!party) {
    return false;
  }
  return party.state === PartyStates.arrived;
}

export function isPartyPartiallyArrived(party?: Service.Party): boolean {
  if (!party) {
    return false;
  }
  return party.state === PartyStates.partiallyarrived;
}

export function isPartyQuotedOriginalWalkIn(party: Service.Party) {
  return isPartyOriginallyWalkIn(party) && !_isNil(party.quotedTimeMinutes);
}

export function isPartyQuotedWalkIn(party: Service.Party) {
  return isPartyWalkIn(party) && !_isNil(party.quotedTimeMinutes);
}

export function isPartyWalkIn(party?: Service.Party): party is Service.WalkInParty {
  return !!party && party.type === 'WalkIn';
}

export function isPartyOnlineWalkIn(party?: Service.Party): party is Service.WalkInParty {
  return isPartyWalkIn(party) && !!party?.walkInPartyDescription?.addedOnline;
}

export function isPartyAssociatedWalkIn(party?: Service.Party): party is Service.AssociatedWalkIn {
  return !!party && party.type === 'WalkIn' && party.walkInType === 'Associated';
}

export function isPartyUnassociatedWalkIn(party?: Service.Party): party is Service.WalkIn {
  return !!party && party.type === 'WalkIn' && party.walkInType === 'WalkIn';
}

export function isPartyOriginallyWalkIn(party?: Service.Party): boolean {
  return !!party && party.id < 0;
}

export function isPartyPurchase(party?: Service.Party): party is Service.PurchaseParty {
  return !!party && party.partyType.partyType === 'PurchaseParty';
}

export function hasBusinessProfiles(
  party: Service.Party
): party is Service.AssociatedWalkIn | Service.PurchaseParty {
  return party.type === 'Booking' || (party.type === 'WalkIn' && party.walkInType === 'Associated');
}

export function getPartyEmail(party: RealtimePartyDetails): string {
  return oc(party).patronInfo.email(oc(party).email(''));
}

export function getPartyPhone(party: RealtimePartyDetails): string {
  return oc(party).patronInfo.phone(oc(party).phone(''));
}

interface INotesMap {
  visitNote?: Partial<dashboard.AuditedNote>;
  visitDietaryNote?: Partial<dashboard.AuditedNote>;
  guestNote?: Partial<dashboard.AuditedNote> | dashboard.IAuditedNote;
  guestDietaryNote?: Partial<dashboard.AuditedNote> | dashboard.IAuditedNote;
}

export interface ILoyaltyProgram {
  purchaseLoyaltyProgramCardNumber?: string;
  guestLoyaltyProgramCardNumber?: string;
  loyaltyPointsRedeemed?: number;
  loyaltyProgramPointAccrualCents?: number;
  canAccrueLoyaltyProgramPoints?: boolean;
}

export function getPartyNotes(party: Service.Party): INotesMap {
  if (party.type === 'WalkIn' && party.walkInType !== 'Associated') {
    return {
      visitNote: undefined,
      visitDietaryNote: undefined,
      guestNote: undefined,
      guestDietaryNote: undefined,
    };
  }
  const { purchase } = party;
  const currentBusinessNotes = oc(party).currentBusinessGuestProfile.note([]);
  const businessGroupNotes = oc(party).primaryDinerProfile.businessGroupGuestProfile.note(
    []
  ) as dashboard.AuditedNote[];

  const businessGuestNote = findNoteWithNoteType('GENERAL', businessGroupNotes);
  const businessGuestDietaryNote = findNoteWithNoteType('DIETARY', businessGroupNotes);
  const guestNote = findNoteWithNoteType('GENERAL', currentBusinessNotes);
  const guestDietaryNote = findNoteWithNoteType('DIETARY', currentBusinessNotes);

  return {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    visitNote: visitGeneralNotes(purchase!),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    visitDietaryNote: visitDietaryNotes(purchase!),
    guestNote: guestNote || businessGuestNote,
    guestDietaryNote: guestDietaryNote || businessGuestDietaryNote,
  };
}

export function getLoyaltyProgram(options: {
  purchase?: core.PastPurchase;
  primaryDinerProfile?: dashboard.IGuestProfile;
}): ILoyaltyProgram {
  const { purchase, primaryDinerProfile } = options;
  if (!purchase && !primaryDinerProfile) {
    return {};
  }
  const loyalty: ILoyaltyProgram = {};

  if (purchase) {
    loyalty.purchaseLoyaltyProgramCardNumber = oc(purchase).loyaltyProgramCardNumber();
    loyalty.loyaltyProgramPointAccrualCents = oc(purchase).loyaltyProgramPointAccrualCents();
    loyalty.canAccrueLoyaltyProgramPoints = oc(purchase).canAccrueLoyaltyProgramPoints();
    loyalty.loyaltyPointsRedeemed = purchase ? getLoyaltyPointsRedeemed(purchase) : 0;
  }

  if (primaryDinerProfile) {
    loyalty.guestLoyaltyProgramCardNumber =
      oc(primaryDinerProfile).loyaltyProgramCardNumber() || undefined;
  }

  return loyalty;
}

export function getPartyNotesForPos(party: Service.Party): string {
  const noteParts = [];
  const notes = getPartyNotes(party);
  if (party.shortNotes) {
    noteParts.push(party.shortNotes);
  }
  if (oc(notes).visitNote.note()) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    noteParts.push(`Visit note: ${notes.visitNote!.note}`);
  }
  if (oc(notes).visitDietaryNote.note()) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    noteParts.push(`Visit dietary note: ${notes.visitDietaryNote!.note}`);
  }
  if (oc(notes).guestNote.note()) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    noteParts.push(`Guest note: ${notes.guestNote!.note}`);
  }
  if (oc(notes).guestDietaryNote.note()) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    noteParts.push(`Guest dietary note: ${notes.guestDietaryNote!.note}`);
  }
  return _join(noteParts, '\n');
}

export function getPartyTags(party: Service.Party): string[] {
  const visitTags = oc(party).visit.tag([]);
  const guestTags = hasBusinessProfiles(party)
    ? getTagsForProfile(oc(party).primaryDinerProfile())
    : [];
  return _union(visitTags, guestTags);
}

/**
 * Only manually-sent text messages that have been successfully sent or received should cause an icon to appear.
 * We can ignore all other messages. For example...
 *
 * We don't want to show a "sent" icon for the message that gets automatically sent after a patron gets added to the waitlist.
 *
 * We don't want to show any icon for the interim and failure states, like "QUEUED", "SENDING", "RECEIVING", "FAILED", etc.
 * The visuals for these states have not been defined yet.
 *
 * Filtering by messageType === 'SERVICE_TEXT' gets rid of auto-sent messages.
 * Ffiltering by status gets rid of the states that we don't care about.
 */
export function textMessageHasStatusIcon(textMessage: dashboard.TextMessage) {
  return (
    textMessage.messageType === 'SERVICE_TEXT' && ['SENT', 'RECEIVED'].includes(textMessage.status)
  );
}

/**
 * the calendar item will either be a walkin calendar item or a booked purchase
 */
export function getPartyForCalendarItem(
  calendarItem: CalendarItem,
  parties: Service.Party[]
): Service.Party | undefined {
  if (calendarItem.state !== 'SOLD') {
    return undefined;
  }

  if (calendarItem instanceof WalkIn) {
    //its a walkin, look based on ids only
    return _find<Service.Party>(parties, (party) => {
      return party.id === calendarItem.party.id;
    });
  }
  if (calendarItem.purchase.visit?.walkInId) {
    return _find<Service.Party>(parties, (party) => {
      return party.id === calendarItem.purchase.visit?.walkInId;
    });
  }

  const purchase = calendarItem.purchase;

  return getPartyForPurchase(purchase, parties);
}

export function getPartyForPurchaseOrderId(
  purchaseId: number,
  parties: Service.Party[]
): Service.Party | undefined {
  return _find<Service.Party>(parties, (party) => {
    if (_isNil(party.purchase)) {
      return false;
    }
    if (!_isNil(party.purchase.originalPurchaseId)) {
      return purchaseId === party.purchase.originalPurchaseId;
    }
    if (!_isNil(party.purchase.id)) {
      return purchaseId === party.purchase.id;
    }
    return false;
  });
}

export function getPartyForPurchase(
  purchase: core.PastPurchase,
  parties: Service.Party[]
): Service.Party | undefined {
  return _find<Service.Party>(parties, (party) => {
    if (_isNil(party.purchase)) {
      return false;
    }
    if (!_isNil(purchase.originalPurchaseId) && !_isNil(party.purchase.originalPurchaseId)) {
      return purchase.originalPurchaseId === party.purchase.originalPurchaseId;
    }
    if (!_isNil(purchase.id) && !_isNil(party.purchase.id)) {
      return purchase.id === party.purchase.id;
    }
    return false;
  });
}

export function getPartyForAbstractBooking(
  booking: AbstractBooking,
  parties: PartyIdToPartyMap
): Service.Party | undefined {
  return parties[booking.party?.id];
}

// yes! the best interface names come from service based things...
export interface PartyTimes {
  arrivalTime?: number;
  expectedReservationTime?: string;
  quotedTimeMinutes?: number;
  seatedTime?: number;
  walkInCreatedTimeSec?: number;
}

export function inferServiceTypeForWalkInParty(
  partyTimes: PartyTimes,
  serviceObjectsByDay: any,
  timeZone: string
): core.Service.Type | undefined {
  if (partyTimes.seatedTime) {
    const seatedTimeInstant = Instant.ofEpochSecond(partyTimes.seatedTime, 0);
    const timeToCompare = LocalDateTime.ofInstant(seatedTimeInstant, ZoneId.of(timeZone));
    return getInferredServiceTypeString(serviceObjectsByDay, timeToCompare);
  } else if (partyTimes.expectedReservationTime) {
    // If party has an expected time, use that for the service inference.
    const timeToCompare = fastParseDateTime(partyTimes.expectedReservationTime);
    return getInferredServiceTypeString(serviceObjectsByDay, timeToCompare);
  } else if (partyTimes.arrivalTime && !_isNil(partyTimes.quotedTimeMinutes)) {
    // If party has a quoted time (but not an expected for some reason), use arrival + quoted.
    const quotedTimeInstant = Instant.ofEpochSecond(partyTimes.arrivalTime, 0).plus(
      partyTimes.quotedTimeMinutes,
      ChronoUnit.MINUTES
    );
    const timeToCompare = LocalDateTime.ofInstant(quotedTimeInstant, ZoneId.of(timeZone));
    return getInferredServiceTypeString(serviceObjectsByDay, timeToCompare);
  } else if (partyTimes.arrivalTime && _isNil(partyTimes.quotedTimeMinutes)) {
    // If party only has arrival, use that.
    const arrivalTimeInstant = Instant.ofEpochSecond(partyTimes.arrivalTime, 0);
    const timeToCompare = LocalDateTime.ofInstant(arrivalTimeInstant, ZoneId.of(timeZone));
    return getInferredServiceTypeString(serviceObjectsByDay, timeToCompare);
  } else if (partyTimes.walkInCreatedTimeSec) {
    // Final fallback is when they were created
    const createdTimeInstant = Instant.ofEpochSecond(partyTimes.walkInCreatedTimeSec, 0);
    const timeToCompare = LocalDateTime.ofInstant(createdTimeInstant, ZoneId.of(timeZone));
    return getInferredServiceTypeString(serviceObjectsByDay, timeToCompare);
  } else {
    return undefined;
  }
}

export function inferServiceTypeForPartyWithPurchase(
  party: Service.PartyFromElm,
  purchase: core.PastPurchase | undefined,
  serviceObjectsByDay: any
): core.Service.Type | undefined {
  if (party.expectedReservationTime) {
    const timeToCompare = fastParseDateTime(party.expectedReservationTime);
    return getInferredServiceTypeString(serviceObjectsByDay, timeToCompare);
  } else if (!purchase) {
    return undefined;
  } else {
    return inferServiceTypeForPurchase(purchase, serviceObjectsByDay);
  }
}

export function inferServiceTypeForPurchase(
  purchase: { ticketDateTime: string },
  serviceObjectsByDay?: IServiceObject[]
): core.Service.Type | undefined {
  if (purchase.ticketDateTime) {
    const timeToCompare = fastParseDateTime(purchase.ticketDateTime);
    const inferredServiceType = getInferredServiceTypeString(serviceObjectsByDay, timeToCompare);
    return inferredServiceType;
  } else {
    return undefined;
  }
}

export function findStateDescriptionForParty(
  party: Service.Party,
  stateDescriptions: Service.StateDescription[]
) {
  return _find(
    stateDescriptions,
    (statePicked) =>
      statePicked.secondaryValue === party.secondaryState &&
      statePicked.primaryState === party.state
  );
}

export function findPrimaryStateInList(
  stateDescriptions: Service.StateDescription[],
  primaryState: string
): Service.StateDescription | undefined {
  return _find(
    stateDescriptions,
    (findState) => _isNil(findState.secondaryValue) && findState.primaryState === primaryState
  );
}

export function getStateGroup(state: string) {
  switch (state) {
    case 'EXPECTED':
    case 'PARTIALLY_ARRIVED':
    case 'PARTIALLY_SEATED':
    case 'ARRIVED':
      return PartyStates.expected;
    case 'SEATED':
      return PartyStates.seated;
    default:
      return state;
  }
}

/**
 * @todo - partiesSelector contains similar logic, changes to this logic should
 * consider partiesSelector and we should consolidate when possible
 */
export function findStateDescriptionForStates(
  stateDescriptions: Service.StateDescription[] = [],
  primaryState: string,
  secondaryState?: string
): Service.StateDescription | undefined {
  const nullableSecondaryState = secondaryState ?? null;
  let defaultMatch;

  for (const stateDescription of stateDescriptions) {
    if (isExactMatch(stateDescription, primaryState, nullableSecondaryState)) {
      return stateDescription;
    }
    if (isDefaultMatch(stateDescription, primaryState)) {
      defaultMatch = stateDescription;
    }
  }

  return defaultMatch;
}

const isExactMatch = (
  stateDescription: Service.StateDescription,
  primaryState: string,
  secondaryState: string | null
) =>
  stateDescription.primaryState === primaryState &&
  stateDescription.secondaryValue === secondaryState;

const isDefaultMatch = (stateDescription: Service.StateDescription, primaryState: string) =>
  stateDescription.primaryState === primaryState && stateDescription.secondaryValue === null;

export function generateStateSelectorStatus(party: Service.Party): StateSelectorStatus {
  const { tableString, state } = party;
  if (isPartyExpected(party) && !tableString) {
    return 'EXPECTED_NO_TABLE';
  } else if (isPartyExpected(party) && tableString) {
    return 'EXPECTED_TABLE';
  } else if (isPartySeated(party) || isPartyPartiallySeated(party)) {
    return 'SEATED';
  } else if (state === PartyStates.left && tableString) {
    return 'FINISHED_TABLE';
  } else if (state === PartyStates.left && !tableString) {
    return 'FINISHED_NO_TABLE';
  } else if (state === PartyStates.canceled) {
    return 'CANCELLED';
  } else {
    return 'NO_SHOW';
  }
}

export function getNextStatusIndex(party: Service.Party, options: Service.StateDescription[]) {
  const selectedOptionIndex = _findIndex(options, (stateOption: Service.StateDescription) => {
    return stateOption.stateName === party.stateDescription.stateName;
  });

  return (selectedOptionIndex + 1) % options.length;
}

export function getButtonActions(
  party: Service.Party | undefined,
  secondaryStates: {
    [primaryState: string]: Service.StateDescription[];
  },
  seatParty?: (party: Service.Party) => void,
  finishParty?: (party: Service.Party) => void
): {
  buttonText: 'Finish' | 'Seat' | undefined;
  buttonAction(event: React.MouseEvent<HTMLElement>): void;
  buttonIcon: any | undefined;
  buttonColorClassname?: string;
  buttonBorderClassname?: string;
  isDisabled: boolean;
} {
  if (!party) {
    return {
      buttonText: undefined,
      buttonAction: _noop,
      buttonIcon: IconPersonCheck,
      isDisabled: false,
    };
  }

  let finishedState;
  let seatedState;
  if (secondaryStates) {
    seatedState = secondaryStates.SEATED.find((state) => _isNil(state.secondaryValue));
    finishedState = secondaryStates.LEFT.find((state) => _isNil(state.secondaryValue));
  }

  let finishedStateClassname;
  let finishedStateBorderClassname;

  if (finishedState) {
    finishedStateClassname = finishedState.className;
    finishedStateBorderClassname = finishedState.borderClassName;
  }

  let seatedStateClassname;
  let seatedStateBorderClassnanme;
  if (seatedState) {
    seatedStateClassname = seatedState.className;
    seatedStateBorderClassnanme = seatedState.borderClassName;
  }

  let buttonText: 'Finish' | 'Seat' | undefined;
  let buttonAction = _noop;
  let buttonIcon;
  let isDisabled = false;
  switch (party.state) {
    case 'SEATED': {
      buttonText = 'Finish';
      buttonAction = () => finishParty && finishParty(party);
      buttonIcon = IconPersonCheck;
      break;
    }
    case 'EXPECTED':
    case 'ARRIVED':
    case 'PARTIALLY_ARRIVED':
    case 'PARTIALLY_SEATED': {
      buttonText = 'Seat';
      buttonAction = () => seatParty && seatParty(party);
      buttonIcon = IconArrowDownCircle;
      isDisabled = party.ticketDateTime
        ? LocalDateTime.parse(party.ticketDateTime).toLocalDate().isAfter(LocalDate.now())
        : false;
      break;
    }
    case 'CANCELED':
    case 'LEFT':
    case 'NO_SHOW':
      return {
        buttonText: undefined,
        buttonAction: _noop,
        buttonIcon: undefined,
        isDisabled,
      };
    default:
      return assertNever(party.state);
  }

  const buttonColorClassname =
    _isEmpty(oc(party).tables([])) || _isUndefined(buttonText)
      ? undefined
      : buttonText === 'Seat'
        ? seatedStateClassname
        : finishedStateClassname;
  const buttonBorderClassname =
    _isEmpty(oc(party).tables([])) || _isUndefined(buttonText)
      ? undefined
      : buttonText === 'Seat'
        ? seatedStateBorderClassnanme
        : finishedStateBorderClassname;

  return {
    buttonText,
    buttonAction,
    buttonIcon,
    buttonColorClassname,
    buttonBorderClassname,
    isDisabled,
  };
}

export const displayStatus = (party: Service.Party) => {
  switch (party.state) {
    case 'SEATED':
      return (
        party.seatedTime && (
          <span>
            Seated at <DisplayTime timeValue={Instant.ofEpochSecond(party.seatedTime)} />
          </span>
        )
      );

    case 'ARRIVED':
    case 'PARTIALLY_ARRIVED':
    case 'PARTIALLY_SEATED':
      return (
        party.arrivalTime && (
          <span>
            Arrived at <DisplayTime timeValue={Instant.ofEpochSecond(party.arrivalTime)} />
          </span>
        )
      );

    case 'EXPECTED':
    case 'CANCELED':
    case 'NO_SHOW':
      return '';

    case 'LEFT':
      return (
        party.leftTime && (
          <span>
            Left at <DisplayTime timeValue={Instant.ofEpochSecond(party.leftTime)} />
          </span>
        )
      );
    default:
      return assertNever(party.state);
  }
};

export const getShouldEnablePartyNotifyIndicator = (party?: Service.Party) => {
  if (
    !party ||
    (party.purchase?.originatedAt !== 'VISIT_EDIT' &&
      party.purchase?.originatedAt !== 'VISIT_EDIT_DASHBOARD')
  ) {
    return false;
  }
  if (getShouldEnablePartyNotifyIndicatorFired(party) || party.hasUnreadTextMessage) {
    return true;
  }
  return false;
};

export const getShouldEnablePartyNotifyIndicatorFired = (party?: Service.Party) => {
  if (
    !party ||
    (party.purchase?.originatedAt !== 'VISIT_EDIT' &&
      party.purchase?.originatedAt !== 'VISIT_EDIT_DASHBOARD')
  ) {
    return false;
  }
  const unfiredItem = _find(party.purchase?.purchasedOption, (option) => !option.sentToKitchen);
  if (unfiredItem) {
    return true;
  }
  return false;
};

export const canMarkPartyAsLeft = (party?: Service.Party) => {
  if (!party) {
    return false;
  }
  if (!party.purchase) {
    return true;
  }

  const moneyLeftOnTablesideParty =
    (party.purchase.originatedAt === 'VISIT_EDIT' ||
      party.purchase?.originatedAt === 'VISIT_EDIT_DASHBOARD') &&
    party.purchase.amountDueCents > 0;

  return !moneyLeftOnTablesideParty;
};

export const isVisitEditParty = (party?: Service.Party) => {
  if (!party) {
    return false;
  }
  if (!party.purchase) {
    return false;
  }

  return (
    party.purchase.originatedAt === 'VISIT_EDIT' ||
    party.purchase.originatedAt === 'VISIT_EDIT_DASHBOARD'
  );
};

export const onFinishPartyCheck = (
  party: Service.Party,
  onUpdatePartyState: (
    party: Service.Party,
    partyState: string,
    secondaryState: string | null
  ) => void
) => {
  confirm
    .show(
      `You haven’t collected payment from this party yet. Are you sure you’d like to mark them as finished?`
    )
    .then(() => onUpdatePartyState(party, PartyStates.left, null))
    .catch(_noop);
  return;
};

export const getPartyTime = (
  party: Service.Party,
  timeZone: string,
  showDeliveryOrderAtPickupTime = false,
  shouldSortReservationsBySeatedTime = false
): LocalDateTime => {
  const seatedTimeSeconds = party.seatedTime;
  const arrivalTimeSeconds = party.arrivalTime;

  const hasPartyBeenSeated =
    party.seatedTime &&
    !(
      party.state === 'CANCELED' ||
      party.state === 'NO_SHOW' ||
      party.state === 'PARTIALLY_SEATED'
    );

  if (
    !isPartyQuotedWalkIn(party) &&
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    isValidDateTimeString(party.expectedReservationTime!) &&
    (!shouldSortReservationsBySeatedTime || !hasPartyBeenSeated)
  ) {
    const ticketType = party.purchase?.ticketType;
    return adjustReservationTime(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      party.expectedReservationTime!,
      isThirdPartyDeliveryVariety(ticketType) ||
        (isDeliveryVariety(ticketType?.variety) && showDeliveryOrderAtPickupTime)
        ? 30
        : 0
    );
  } else if (seatedTimeSeconds || arrivalTimeSeconds) {
    const dateTime = toLocalDateTime(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      Instant.ofEpochSecond(seatedTimeSeconds! || arrivalTimeSeconds!, 0),
      timeZone
    );
    return roundTo15MinInterval(dateTime);
  } else {
    // This should not happen, but seems to be happening in tests.
    return roundTo15MinInterval(LocalDateTime.now());
  }
};

export const adjustReservationTime = (reservationTimeString: string, earlyOffsetMinutes = 0) => {
  const dateTime = LocalDateTime.parse(reservationTimeString);
  return roundTo15MinInterval(dateTime.minusMinutes(earlyOffsetMinutes));
};

export const isAssignableAbstractBooking = (
  booking: AbstractBooking,
  ticketTypeVariety?: TicketTypeVariety
) => {
  return bookingIsPurchase(booking)
    ? isAssignableBooking(booking, ticketTypeVariety)
    : isAssignableWalkin(booking);
};

const isAssignableBooking = (purchase: Purchase, ticketTypeVariety?: TicketTypeVariety) => {
  const assignableStates = [
    'EXPECTED',
    'ARRIVED',
    'PARTIALLY_ARRIVED',
    'PARTIALLY_SEATED',
    'SEATED',
  ];

  const primaryState = purchase.party.primaryState;
  return (
    !isOrderVariety(ticketTypeVariety) && primaryState && assignableStates.includes(primaryState)
  );
};

const isAssignableWalkin = (walkIn: WalkInBooking) => {
  return walkIn.party.primaryState === 'SEATED';
};

export const isAssignableCalendarSquare = (square: BookedCalendarSquare | WalkInCalendarSquare) => {
  const { subType, primaryState } = square;

  if (!primaryState) {
    return false;
  }

  return subType === 'WALK_IN'
    ? isTimelineAssignableWalkIn(primaryState)
    : isTimelineAssignableBooking(primaryState, square.purchase);
};

const isTimelineAssignableWalkIn = (state: string) => {
  return state === 'SEATED';
};

export const isTimelineAssignableBooking = (state: string, purchase: core.PastPurchase) => {
  const assignableStates = [
    'EXPECTED',
    'ARRIVED',
    'PARTIALLY_ARRIVED',
    'PARTIALLY_SEATED',
    'SEATED',
  ];
  return !isOrderVariety(purchase.ticketTypeVariety) && assignableStates.includes(state);
};
